import { useState } from 'react'
import { useSession } from '../state/session'

/**
 * "Who shall I tell the photographer these are from?"
 *
 * Shown once, inline, the moment the reviewer makes their FIRST pick — not at
 * the end. That timing is the convention across review and proofing tools: the
 * identity given at the first selection is what the studio's activity list is
 * keyed on, and asking only in the Send panel meant anyone who browsed, or who
 * never finished, stayed anonymous forever. The studio then sees "Reviewer 2"
 * for somebody they emailed by name an hour earlier.
 *
 * Rules it must keep:
 *   · never a gate — Skip is a real answer, and reviewing continues either way
 *   · never a modal — it sits in the flow, above the pick bar, and does not
 *     interrupt the frame they are looking at
 *   · both fields optional — a name alone is friendlier, an address is what
 *     survives them opening the gallery again on another device
 */
export function IdentityPrompt() {
  const shouldAsk = useSession((s) => s.shouldAskIdentity())
  const markAsked = useSession((s) => s.markIdentityAsked)
  const setVoterName = useSession((s) => s.setVoterName)
  const setVoterEmail = useSession((s) => s.setVoterEmail)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  if (!shouldAsk) return null

  const emailLooksValid = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const canSave = Boolean(name.trim() || email.trim()) && emailLooksValid

  const save = () => {
    if (name.trim()) setVoterName(name.trim())
    if (email.trim()) setVoterEmail(email.trim())
    markAsked()
  }

  return (
    <div
      data-testid="identity-prompt"
      className="mb-2.5 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-secondary)] p-3"
    >
      <div className="text-[13px] font-semibold text-[var(--text-primary)]">
        Who should these picks be from?
      </div>
      <p className="mt-0.5 text-[12px] text-[var(--text-tertiary)]">
        Optional — it just means your photographer sees your name instead of “Reviewer 2”.
      </p>
      <div className="mt-2.5 flex flex-col gap-2 sm:flex-row">
        <label className="flex-1">
          <span className="sr-only">Your name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            autoComplete="name"
            maxLength={80}
            data-testid="identity-prompt-name"
            className="h-11 w-full rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-primary)] px-3 text-[13px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--text-tertiary)]"
          />
        </label>
        <label className="flex-1">
          <span className="sr-only">Your email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email (optional)"
            autoComplete="email"
            maxLength={320}
            data-testid="identity-prompt-email"
            className="h-11 w-full rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-primary)] px-3 text-[13px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--text-tertiary)]"
          />
        </label>
      </div>
      {!emailLooksValid && (
        <p role="alert" className="mt-1.5 text-[11.5px] text-[var(--accent-danger,#ef4444)]">
          That address doesn’t look right.
        </p>
      )}
      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          data-testid="identity-prompt-save"
          className="h-11 flex-1 rounded-xl bg-[var(--accent-primary)] text-[13px] font-semibold text-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Save
        </button>
        {/* Skip is a real answer, not a nag to dismiss. A reviewer who does not
            want to say who they are keeps every other capability. */}
        <button
          type="button"
          onClick={markAsked}
          data-testid="identity-prompt-skip"
          className="h-11 rounded-xl px-3.5 text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
        >
          Skip
        </button>
      </div>
    </div>
  )
}
